import type { permissions } from '@/common';
import type { Variants } from '@/common';
import type { Component } from 'vue';
import type { RouteLocationRaw } from 'vue-router/auto';
import {
    RiUserLine as UsersIcon,
    RiGraduationCapLine as StudentsIcon,
    RiMessage2Line,
    RiBuilding4Line as CompanyIcon,
    RiFilePaper2Line as ApplicationsIcon,
    RiShakeHandsLine as PartnersIcon,
    RiMoneyDollarCircleLine as PaymentsIcon,
    RiBookOpenLine as CourseIcon,
    RiDashboardLine,
    RiMedal2Line as PublicPageIcon,
    RiSettings2Line as SettingsIcon,
    RiEmotionHappyLine as ResumeIcon,
    RiFilePaper2Line as TemplatesIcon,
    RiTaskLine as TasksIcon,
    RiServiceLine as ServiceIcon,
    RiTeamLine as SubAgentsIcon,
    RiPulseLine as CommissionsIcon,
    RiFileList3Line as InvoicesIcon,
    RiQrCodeLine as QrCodeIcon,
    RiCalendarCheckLine as BookingsIcon,
    RiMailLine as EmailIcon,
    RiPassportLine as VisaIcon,
} from '@remixicon/vue';

export type BadgeInfo = {
    variant?: Variants;
    text: string;
    type?: 'badge' | 'pill';
};

export type IconInfo = Component | Function;

export type NavItem = {
    label: string;
    description?: string;
    to: RouteLocationRaw;
    children?: undefined;
    permission?: permissions.PermissionLabel;
    anyPermissions?: permissions.PermissionLabel[];
    allPermissions?: permissions.PermissionLabel[];
    activeCheck?: () => boolean;
    badge?: BadgeInfo;
    icon?: IconInfo;
    hide?: boolean;
};

export type NavGroup =
    | ({
          label: string;
          badge?: BadgeInfo;
          icon?: IconInfo;
      } & (
          | {
                to: RouteLocationRaw;
                description?: string;
                children?: undefined;
            }
          | {
                children: NavItem[];
                to?: undefined;
            }
      ))
    | NavItem;

export type NavSection = {
    label: string;
    groups: NavGroup[];
};

export const useRootNavigation = () => {
    const conversationStore = useConversationStore();
    const {
        company,
        user,
        settings,
        selectedStudentId,
        isSuperAdmin,
        isSubAgent,
        agencies,
        campuses,
        authorized_branches,
    } = useCompany();

    const menus = computed((): NavSection[] => {
        const c = company.value;
        if (c == null || user.value == null) {
            return [];
        }
        const firstAgency = agencies.value?.find(a =>
            authorized_branches.value?.some(b => b.id === a.company_branch_id),
        );
        const firstCampus = campuses.value?.find(c =>
            authorized_branches.value?.some(b => b.id === c.company_branch_id),
        );

        //common
        const commonBase: NavItem[] = [
            {
                label: 'Dashboard',
                to: { name: 'dashboard' },
                description: 'Visit your dashboard.',
                icon: RiDashboardLine,
            },
            {
                label: 'Messages',
                to: { name: 'all-conversations' },
                description: 'View all messages.',
                icon: RiMessage2Line,
                badge:
                    conversationStore.unreadMessageIds.length === 0
                        ? undefined
                        : {
                              text: conversationStore.unreadMessageIds.length.toString(),
                              type: 'pill',
                              variant: 'danger',
                          },
            },
        ];

        //student — no Messages; services are view-only ("My Services")
        if (c.company_type === 'student') {
            const studentCommon: NavItem[] = [
                {
                    label: 'Dashboard',
                    to: { name: 'dashboard' },
                    description: 'Visit your dashboard.',
                    icon: RiDashboardLine,
                },
                ...(selectedStudentId.value == null
                    ? []
                    : ([
                          {
                              label: 'Profile',
                              to: { name: 'manage-student', params: { studentId: selectedStudentId.value } },
                              description: 'Manage student profile and applications.',
                          },
                      ] satisfies NavItem[])),
                ...(user.value == null
                    ? []
                    : ([
                          {
                              label: 'Build Resume',
                              to: { name: 'student-resume', params: { studentId: user.value.id } },
                              description: 'Create your Resume.',
                              icon: ResumeIcon,
                          },
                      ] satisfies NavItem[])),
                {
                    label: 'Course Applications',
                    icon: ApplicationsIcon,
                    to: { name: 'manage-applications' },
                    description: 'View all applications.',
                } satisfies NavItem,
                {
                    label: 'Payment Plans',
                    to: { name: 'manage-payment-plans' },
                    description: 'View your payment plans.',
                    icon: PaymentsIcon,
                },
                {
                    label: 'Explore Courses',
                    to: { name: 'explore-courses' },
                    description: 'Explore courses and schools.',
                    icon: CourseIcon,
                },
                {
                    label: 'My Services',
                    to: { name: 'services:manage' },
                    description: 'View your services.',
                    icon: ServiceIcon,
                },
                {
                    label: 'Bookings',
                    icon: BookingsIcon,
                    children: [
                        {
                            label: 'My Bookings',
                            to: { name: 'bookings:list' },
                            description: 'View your appointment bookings.',
                        },
                        {
                            label: 'Book Appointment',
                            to: { name: 'bookings:new' },
                            description: 'Book an appointment with your agent.',
                        },
                    ],
                },
            ];
            return [
                {
                    label: 'Common',
                    groups: studentCommon,
                },
            ];
        }

        // staff

        if (c.company_type === 'staff') {
            return [
                {
                    label: 'Common',
                    groups: commonBase,
                },
                {
                    label: 'Administration',
                    groups: [
                        {
                            label: 'Company',
                            icon: CompanyIcon,
                            to: { name: 'view-company', params: { companyId: c.id } },
                            hide: !isSuperAdmin.value,
                        },
                        {
                            label: 'Branch',
                            icon: CompanyIcon,
                            to: { name: 'manage-branch', params: { branchId: user.value.company_branch_id } },
                            hide: isSuperAdmin.value,
                        },
                        {
                            label: 'Course Applications',
                            to: { name: 'manage-applications' },
                            description: 'View all applications.',
                            icon: ApplicationsIcon,
                        },
                        {
                            label: 'Agencies',
                            children: [
                                {
                                    label: 'List',
                                    to: { name: 'admin-agencies' },
                                    description: 'View all agencies.',
                                },
                            ],
                        },
                        {
                            label: 'Institutions',
                            children: [
                                {
                                    label: 'List',
                                    to: { name: 'admin-schools' },
                                    description: 'View all schools.',
                                },
                            ],
                        },
                        {
                            label: 'Courses',
                            icon: CourseIcon,
                            children: [
                                {
                                    label: 'Courses',
                                    to: { name: 'admin-programs' },
                                    description: 'View all courses.',
                                },
                                {
                                    label: 'Packages',
                                    to: { name: 'admin-packages' },
                                    description: 'View all packages.',
                                },
                            ],
                        },
                        {
                            label: 'Student Center',
                            children: [
                                {
                                    label: 'List',
                                    to: { name: 'admin-students' },
                                    description: 'View all students.',
                                },
                            ],
                        },
                        {
                            label: 'Users',
                            to: { name: 'admin-users' },
                            description: 'Manage users on platform.',
                            icon: UsersIcon,
                            permission: 'viewUsers',
                        },
                        {
                            label: 'Finances',
                            children: [
                                {
                                    label: 'Accounts',
                                    to: { name: 'report-accounts' },
                                    description: 'Report on payments.',
                                    icon: PaymentsIcon,
                                    permission: 'viewAccounts',
                                },
                                {
                                    label: 'Payouts',
                                    to: { name: 'manage-payouts' },
                                    description: 'Payouts.',
                                    icon: PaymentsIcon,
                                    permission: 'viewAccounts',
                                },
                                {
                                    label: 'Payment Verification',
                                    to: { name: 'payments:verification' },
                                    description: 'Review and verify manual payments.',
                                    icon: PaymentsIcon,
                                    permission: 'confirmManualPayment',
                                },
                            ],
                        },
                    ],
                },
                // {
                //     label: 'WebViews',
                //     groups: [
                //         {
                //             label: 'Pages',
                //             children:
                //                 contextStore.definedWebViews?.map(d => ({
                //                     label: d.name,
                //                     description: d.description,
                //                     to: { name: 'edit-webview', params: { webviewId: d.id } },
                //                     permission: 'editWebviews',
                //                 })) ?? [],
                //         },
                //     ],
                // },
                {
                    label: 'Admin',
                    groups: [
                        {
                            label: 'Constants',
                            children: [
                                {
                                    label: 'Document',
                                    to: { name: 'document-types' },
                                    permission: 'editDocumentTypes',
                                },
                                {
                                    label: 'Program Levels',
                                    to: { name: 'program-levels' },
                                    permission: 'editProgramLevels',
                                },
                                {
                                    label: 'Agreement Templates',
                                    to: { name: 'agreement-templates' },
                                    permission: 'editAgreementTemplates',
                                },
                                {
                                    label: 'Qualification Levels',
                                    to: { name: 'qualification-levels' },
                                    permission: 'editQualificationLevels',
                                },
                                {
                                    label: 'Service Templates',
                                    to: { name: 'service-templates:manage' },
                                    icon: TemplatesIcon,
                                },
                            ],
                        },
                        {
                            label: 'Settings',
                            to: { name: 'settings' },
                            permission: 'editSettings',
                            icon: SettingsIcon,
                        },
                        {
                            label: 'Utilities',
                            to: { name: 'admin-utils' },
                            permission: 'editSettings',
                            icon: SettingsIcon,
                        },
                    ],
                },
            ];
        }

        //agencies and schools

        const commonWithPublicPage = [
            ...commonBase,
            ...(company.value == null
                ? []
                : ([
                      {
                          label: 'Public Page',
                          icon: PublicPageIcon,
                          to: { name: 'public-company', params: { companyId: c.id } },
                      },
                  ] satisfies NavItem[])),
        ];

        // school

        if (c.company_type === 'school') {
            return [
                {
                    label: 'Common',
                    groups: commonWithPublicPage,
                },
                {
                    label: 'Administration',
                    groups: [
                        {
                            label: 'Institution',
                            icon: CompanyIcon,
                            to: { name: 'view-company', params: { companyId: c.id } },
                            hide: !isSuperAdmin.value,
                        },
                        ...(firstCampus == null
                            ? []
                            : [
                                  {
                                      label: 'My Campus',
                                      icon: CompanyIcon,
                                      to: { name: 'manage-campus', params: { campusId: firstCampus.id } },
                                  } satisfies NavItem,
                              ]),
                        {
                            label: 'Courses',
                            to: { name: 'manage-programs' },
                            icon: CourseIcon,
                        },
                        {
                            label: 'Packages',
                            to: { name: 'manage-packages' },
                            icon: CourseIcon,
                        },
                        {
                            label: 'Partners',
                            icon: PartnersIcon,
                            children: [
                                {
                                    label: 'My Partners',
                                    to: { name: 'manage-partners' },
                                    permission: 'viewPartners',
                                },
                                {
                                    label: 'Explore Partners',
                                    to: { name: 'explore-agencies' },
                                    permission: 'explorePartners',
                                },
                            ],
                        },
                        {
                            label: 'Course Applications',
                            icon: ApplicationsIcon,
                            to: { name: 'manage-applications' },
                            description: 'View all applications.',
                        } satisfies NavItem,
                        {
                            label: 'Accounts',
                            to: { name: 'report-accounts' },
                            description: 'Report on payments.',
                            permission: 'viewAccounts',
                            icon: PaymentsIcon,
                        } satisfies NavItem,
                        ...(company.value?.company_type !== 'staff'
                            ? []
                            : [
                                  {
                                      label: 'Payouts',
                                      to: { name: 'manage-payouts' },
                                      description: 'Payouts.',
                                      icon: PaymentsIcon,
                                  } satisfies NavItem,
                              ]),
                    ],
                },
            ];
        }

        // agency — sub-agent restricted menu

        if (isSubAgent.value) {
            return [
                {
                    label: 'Common',
                    groups: commonBase,
                },
                {
                    label: 'My Work',
                    groups: [
                        {
                            label: 'Student Center',
                            icon: StudentsIcon,
                            children: [
                                ...(settings.value?.dynamic?.allow_agency_direct_add_student
                                    ? ([
                                          {
                                              label: 'Add Student',
                                              to: { name: 'add-students' },
                                              description: 'Add students.',
                                              permission: 'addStudents',
                                          },
                                      ] satisfies NavItem[])
                                    : ([
                                          {
                                              label: 'Invite Student',
                                              to: { name: 'invite-students' },
                                              description: 'Invite a student to your agency branch.',
                                              permission: 'addStudents',
                                          },
                                      ] satisfies NavItem[])),
                                {
                                    label: 'My Students',
                                    to: { name: 'list-students' },
                                    description: 'View your students.',
                                },
                                {
                                    label: 'Explore Courses',
                                    to: { name: 'explore-courses' },
                                    description: 'Explore courses and enroll students.',
                                    icon: CourseIcon,
                                },
                                {
                                    label: 'Course Applications',
                                    icon: ApplicationsIcon,
                                    to: { name: 'manage-applications' },
                                    description: 'View your applications.',
                                } satisfies NavItem,
                            ] satisfies NavItem[],
                        },
                        {
                            label: 'Visa Services',
                            icon: ServiceIcon,
                            children: [
                                {
                                    label: 'Manage Services',
                                    to: { name: 'services:manage' },
                                    description: 'Manage services.',
                                },
                                {
                                    label: 'Service Templates',
                                    to: { name: 'service-templates:manage' },
                                    description: 'Manage service templates.',
                                    icon: TemplatesIcon,
                                },
                                {
                                    label: 'Agreement Templates',
                                    to: { name: 'agreement-templates' },
                                    description: 'Manage agreement templates.',
                                    icon: TemplatesIcon,
                                },
                            ],
                        },
                        {
                            label: 'My Commissions',
                            to: { name: 'sub-agents:commissions' },
                            description: 'View your commission earnings.',
                            icon: CommissionsIcon,
                        },
                        {
                            label: 'Student Invoices',
                            to: { name: 'sub-agents:invoices' },
                            description: 'View invoices for your students.',
                            icon: InvoicesIcon,
                        },
                        {
                            label: 'My Referral Link',
                            to: { name: 'sub-agents:referral' },
                            description: 'Share your referral link with students.',
                            icon: QrCodeIcon,
                        },
                        {
                            label: 'Bookings',
                            icon: BookingsIcon,
                            children: [
                                {
                                    label: 'My Bookings',
                                    to: { name: 'bookings:list' },
                                    description: 'View appointment bookings.',
                                },
                                {
                                    label: 'Book for Student',
                                    to: { name: 'bookings:new' },
                                    description: 'Book an appointment on behalf of a student.',
                                },
                                {
                                    label: 'Working Hours',
                                    to: { name: 'bookings:availability' },
                                    description: 'Set your available hours.',
                                },
                            ],
                        },
                    ],
                },
            ];
        }

        // agency — full menu

        return [
            {
                label: 'Common',
                groups: [
                    ...commonBase,
                    {
                        label: 'Email',
                        icon: EmailIcon,
                        children: [
                            {
                                label: 'Inbox',
                                to: { name: 'email:inbox' },
                                description: 'View and send emails.',
                            },
                            {
                                label: 'Settings',
                                to: { name: 'email:settings' },
                                description: 'Configure email settings.',
                            },
                        ],
                    },
                ],
            },
            {
                label: 'Students & Courses',
                groups: [
                    {
                        label: 'Student Center',
                        icon: StudentsIcon,
                        children: [
                            ...(settings.value?.dynamic?.allow_agency_direct_add_student
                                ? ([
                                      {
                                          label: 'Add Student',
                                          to: { name: 'add-students' },
                                          description: 'Add students.',
                                          permission: 'addStudents',
                                      },
                                  ] satisfies NavItem[])
                                : ([
                                      {
                                          label: 'Invite Student',
                                          to: { name: 'invite-students' },
                                          description: 'Invite a student to your agency branch.',
                                          permission: 'addStudents',
                                      },
                                  ] satisfies NavItem[])),
                            {
                                label: 'Student List',
                                to: { name: 'list-students' },
                                description: 'View all students.',
                            },
                            {
                                label: 'Explore Courses',
                                to: { name: 'explore-courses' },
                                description: 'Explore courses and enrol students.',
                                icon: CourseIcon,
                            },
                        ] satisfies NavItem[],
                    },
                    {
                        label: 'Course Applications',
                        icon: ApplicationsIcon,
                        to: { name: 'manage-applications' },
                        description: 'View all applications.',
                    } satisfies NavItem,
                    {
                        label: 'Visa Services',
                        icon: ServiceIcon,
                        children: [
                            {
                                label: 'Manage Services',
                                to: { name: 'services:manage' },
                                description: 'Manage services.',
                            },
                            {
                                label: 'Service Templates',
                                to: { name: 'service-templates:manage' },
                                description: 'Manage service templates.',
                                icon: TemplatesIcon,
                            },
                            {
                                label: 'Agreement Templates',
                                to: { name: 'agreement-templates' },
                                description: 'Manage agreement templates.',
                                icon: TemplatesIcon,
                            },
                        ],
                    },
                    {
                        label: 'Visa Processing',
                        to: { name: 'visa-processing' },
                        description: 'Manage visa applications.',
                        icon: VisaIcon,
                    },
                ],
            },
            {
                label: 'Operations',
                groups: [
                    {
                        label: 'Tasks',
                        to: { name: 'tasks:dashboard' },
                        description: 'Manage tasks across your company.',
                        icon: TasksIcon,
                    },
                    {
                        label: 'Bookings',
                        icon: BookingsIcon,
                        children: [
                            {
                                label: 'My Bookings',
                                to: { name: 'bookings:list' },
                                description: 'View appointment bookings.',
                            },
                            {
                                label: 'Book for Student',
                                to: { name: 'bookings:new' },
                                description: 'Book an appointment on behalf of a student.',
                            },
                            {
                                label: 'Working Hours',
                                to: { name: 'bookings:availability' },
                                description: 'Set your available hours.',
                            },
                        ],
                    },
                    {
                        label: 'Partners',
                        icon: PartnersIcon,
                        children: [
                            {
                                label: 'My Partners',
                                to: { name: 'manage-partners' },
                                permission: 'viewPartners',
                            },
                            {
                                label: 'Explore Partners',
                                to: { name: 'explore-schools' },
                                permission: 'explorePartners',
                            },
                        ],
                    },
                    {
                        label: 'Sub-Agents',
                        to: { name: 'sub-agents:list' },
                        description: 'Manage your sub-agents.',
                        icon: SubAgentsIcon,
                        permission: 'manageUsers',
                    } satisfies NavItem,
                ],
            },
            {
                label: 'Finance',
                groups: [
                    {
                        label: 'Accounts',
                        to: { name: 'report-accounts' },
                        description: 'Report on payments.',
                        icon: PaymentsIcon,
                        permission: 'viewAccounts',
                    } satisfies NavItem,
                    {
                        label: 'Payment Verification',
                        to: { name: 'payments:verification' },
                        description: 'Review and verify manual payments.',
                        icon: PaymentsIcon,
                        permission: 'confirmManualPayment',
                    } satisfies NavItem,
                    ...(company.value?.company_type !== 'staff'
                        ? []
                        : [
                              {
                                  label: 'Payouts',
                                  to: { name: 'manage-payouts' },
                                  description: 'Manage payouts.',
                                  icon: PaymentsIcon,
                              } satisfies NavItem,
                          ]),
                ],
            },
            {
                label: 'Organisation',
                groups: [
                    {
                        label: 'Company',
                        icon: CompanyIcon,
                        to: { name: 'view-company', params: { companyId: c.id } },
                        hide: !isSuperAdmin.value,
                    },
                    ...(firstAgency == null
                        ? []
                        : [
                              {
                                  label: 'My Branch',
                                  icon: CompanyIcon,
                                  to: { name: 'manage-agency', params: { agencyId: firstAgency.id } },
                              } satisfies NavItem,
                          ]),
                    ...(company.value == null
                        ? []
                        : ([
                              {
                                  label: 'Public Page',
                                  icon: PublicPageIcon,
                                  to: { name: 'public-company', params: { companyId: c.id } },
                              },
                          ] satisfies NavItem[])),
                    {
                        label: 'Document Drafts',
                        to: { name: 'document-drafts:manage' },
                        description: 'Manage document drafts.',
                        icon: TemplatesIcon,
                    },
                ],
            },
        ];
    });
    return { menus };
};
